import { format } from 'date-fns';

import { EmailExportedReport } from '@futrli-sdk/report_scheduling';

import { ApiView, ApiViewState, getDefaultApiViewState } from '../../../core/ApiView';
import { exportReportToPdfMutation, ExportReportToPdfInput } from '../../../mutations/reportActions';
import {
  emailExportedReportMutation,
  EmailExportedReportInput,
  sendReportPdfMutation,
  SendReportPdfInput
} from '../../../mutations/reportScheduleActions';

export interface ReportDocumentViewState extends ApiViewState {
  emailSent: boolean;
  errorExporting: Error | null;
  errorSendingEmail: Error | null;
  exportedReportId?: string;
  isExporting: boolean;
  isSendingEmail: boolean;
}

interface EmailReportDetails {
  body: string;
  subject: string;
  users: EmailExportedReport['users'];
}

export type EmailReportInput =
  | (EmailReportDetails & { exportedReportId: string; reportId?: never; reportName?: never })
  | (EmailReportDetails & { exportedReportId?: never; reportId: string; reportName: string });

export class ReportDocumentView extends ApiView {
  private emailExportedMutation: ReturnType<typeof emailExportedReportMutation>;
  private exportPdfMutation: ReturnType<typeof exportReportToPdfMutation>;
  private sendPdfMutation: ReturnType<typeof sendReportPdfMutation>;

  public state: ReportDocumentViewState = {
    ...getDefaultApiViewState(),
    emailSent: false,
    errorExporting: null,
    errorSendingEmail: null,
    exportedReportId: undefined,
    isExporting: false,
    isSendingEmail: false
  };

  constructor(workspaceId: string, organisationId: string, isConsolidation = false) {
    super();

    this.emailExportedMutation = this.addMutation(
      emailExportedReportMutation(workspaceId, organisationId, isConsolidation)
    );
    this.exportPdfMutation = this.addMutation(exportReportToPdfMutation(workspaceId, organisationId, isConsolidation));
    this.sendPdfMutation = this.addMutation(sendReportPdfMutation(workspaceId, organisationId, isConsolidation));

    this.updateState();
  }

  // ***************
  // *  Public API *
  // ***************
  public emailExportedReport(data: EmailExportedReportInput) {
    return this.emailExportedMutation.mutate(data);
  }

  public emailReport(data: EmailReportInput) {
    if (data.exportedReportId) {
      return this.emailExportedReport({
        exportedReportId: data.exportedReportId,
        data: {
          body: data.body,
          subject: data.subject,
          users: data.users
        }
      });
    }

    return this.sendReportPdf({
      data: {
        name: data.reportName,
        report: data.reportId,
        period: 'do-not-repeat',
        start_date: format(new Date(), 'yyyy-MM-dd'),
        email_subject: data.subject,
        email_body: data.body,
        users: data.users,
        approval_email_user: false,
        end_type: 'does-not-end'
      }
    });
  }

  public exportReportToPdf(data: ExportReportToPdfInput) {
    return this.exportPdfMutation.mutate(data);
  }

  public resetMutations() {
    this.emailExportedMutation.reset();
    this.exportPdfMutation.reset();
    this.sendPdfMutation.reset();
    this.updateState();
  }

  public sendReportPdf(data: SendReportPdfInput) {
    return this.sendPdfMutation.mutate(data);
  }

  // ****************************
  // * Private / Protected API *
  // ****************************
  protected getNewState(): ReportDocumentViewState {
    const emailStatus = this.emailExportedMutation.getCurrentResult().status;
    const sendStatus = this.sendPdfMutation.getCurrentResult().status;

    return {
      ...super.getNewState(),
      emailSent: emailStatus === 'success' || sendStatus === 'success',
      errorExporting: this.exportPdfMutation.getCurrentResult().error,
      errorSendingEmail:
        this.emailExportedMutation.getCurrentResult().error ?? this.sendPdfMutation.getCurrentResult().error,
      exportedReportId: this.exportPdfMutation.getCurrentResult().data?.id,
      isExporting: this.exportPdfMutation.getCurrentResult().status === 'pending',
      isSendingEmail: emailStatus === 'pending' || sendStatus === 'pending'
    };
  }
}
